/*
 * Showroom status (Visit panel and footer dot) — progressive refinement only.
 *
 * The server already rendered the DAY-level state from the Worker feed, which
 * is the most a cached page can honestly claim. It also rendered today's
 * timeline (snippets/ktechs-showroom-timeline): phases that each say what is
 * true until a minute of the day, with their own state, label and line. This
 * script shows the first phase still ahead in Singapore time. It is the only
 * thing that ever turns the pulsing dot on: only the open-now phase pulses,
 * and a pulse next to "closed" or "unconfirmed" would signal activity the
 * showroom doesn't have.
 *
 * Every phase carries all of its words, so running twice (the footer and the
 * Visit section both load this file) just applies the same phase again. If
 * anything here fails, the server-rendered state stands.
 *
 * The Visit calendar's days are buttons: picking one puts that day's date and
 * summary (rendered by the server into the button) in the panel's own two
 * lines, and picking Today puts back what the panel said about today. A
 * meetup's ticket link follows the line it belongs to.
 */
(function () {
  var panels = document.querySelectorAll('[data-ktechs-showroom][data-timeline]');

  function nowInZone(tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false
      }).formatToParts(new Date());
      var h = 0, m = 0;
      parts.forEach(function (p) {
        if (p.type === 'hour') h = parseInt(p.value, 10) % 24;
        if (p.type === 'minute') m = parseInt(p.value, 10);
      });
      return h * 60 + m;
    } catch (e) {
      return null;
    }
  }

  Array.prototype.forEach.call(panels, function (panel) {
    var phases;
    try {
      phases = JSON.parse(panel.getAttribute('data-timeline'));
    } catch (e) {
      return;
    }
    var mins = nowInZone(panel.getAttribute('data-timezone') || 'Asia/Singapore');
    if (!Array.isArray(phases) || mins === null) return;

    var phase = null;
    for (var i = 0; i < phases.length; i++) {
      if (mins < phases[i].until) {
        phase = phases[i];
        break;
      }
    }
    if (!phase || !phase.state) return;

    var label = panel.querySelector('[data-showroom-label]');
    var sub = panel.querySelector('[data-showroom-sub]');
    panel.setAttribute('data-state', phase.state);
    if (label && phase.label) label.textContent = phase.label;
    if (sub && phase.sub) sub.textContent = phase.sub;
    showLink(panel.querySelector('[data-showroom-link]'), phase.link);
  });

  // The ticket link shows only with a line that has one.
  function showLink(link, href) {
    if (!link) return;
    if (href) {
      link.setAttribute('href', href);
      link.hidden = false;
    } else {
      link.hidden = true;
    }
  }

  Array.prototype.forEach.call(document.querySelectorAll('[data-showroom-days]'), function (grid) {
    if (grid.hasAttribute('data-showroom-days-ready')) return;
    var panel = grid.closest('.ktechs-showroom[data-ktechs-showroom]');
    var label = panel && panel.querySelector('[data-showroom-label]');
    var sub = panel && panel.querySelector('[data-showroom-sub]');
    var link = panel && panel.querySelector('[data-showroom-link]');
    var announce = panel && panel.querySelector('[data-showroom-announce]');
    var days = Array.prototype.slice.call(grid.querySelectorAll('button[data-day-state]'));
    if (!label || !sub || !days.length) return;
    grid.setAttribute('data-showroom-days-ready', '');

    // Today's own words are whatever the panel says once the clock has had its
    // say, so they are read at the first pick, not rendered twice.
    var today = null;

    // The sentence sits under the days, so when it changes length only the
    // panel's bottom edge moves (merchant, 26 Sep 2026: option 1, eased). The
    // edge eases on the site's own curve and time; a new pick mid-glide starts
    // from the height on screen, not the old one, and reduced motion snaps.
    var line = sub.closest('.ktechs-showroom__sub') || sub;
    var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    var glide = null;

    function setSentence(text, href) {
      var from = line.getBoundingClientRect().height;
      if (glide) glide.cancel();
      sub.textContent = text;
      showLink(link, href);
      var to = line.getBoundingClientRect().height;
      if (from === to || (still && still.matches) || !line.animate) return;
      line.style.overflow = 'hidden';
      var this_glide = line.animate(
        [{ height: from + 'px' }, { height: to + 'px' }],
        { duration: 180, easing: 'cubic-bezier(0.2, 0, 0, 1)' }
      );
      glide = this_glide;
      // A cancelled glide reports late, after the next one has started: only
      // the glide still running may take the clipping off.
      this_glide.onfinish = this_glide.oncancel = function () {
        if (glide !== this_glide) return;
        glide = null;
        line.style.overflow = '';
      };
    }

    function pick(day) {
      if (!today) {
        today = {
          state: panel.getAttribute('data-state'),
          label: label.textContent,
          sub: sub.textContent,
          link: link && !link.hidden ? link.getAttribute('href') : null
        };
      }
      days.forEach(function (d) {
        d.setAttribute('aria-pressed', d === day ? 'true' : 'false');
        d.tabIndex = d === day ? 0 : -1;
      });
      var view = day.hasAttribute('aria-current')
        ? today
        : {
          state: day.getAttribute('data-day-state'),
          label: day.getAttribute('data-day-label'),
          sub: day.getAttribute('data-day-sub'),
          link: day.getAttribute('data-day-link')
        };
      panel.setAttribute('data-state', view.state);
      label.textContent = view.label;
      setSentence(view.sub, view.link);
      // Read out what changed, once, from a region that holds only that: the
      // header and the sentence sit either side of the days, so neither can
      // be the live region without the days being read with it.
      if (announce) announce.textContent = view.label + '. ' + view.sub;
    }

    grid.addEventListener('click', function (event) {
      var day = event.target.closest('button[data-day-state]');
      if (day && grid.contains(day)) pick(day);
    });

    // One Tab stop for the calendar, and the arrow keys pick as they move, as
    // tabs do: the focused day and the picked day are always the same one, so
    // the calendar never shows two outlines. Picking only changes two lines of
    // text, so there is nothing to confirm. Up and Down move a week where the
    // days sit in weeks, and a day where they sit in one row.
    grid.addEventListener('keydown', function (event) {
      var at = days.indexOf(document.activeElement);
      if (at === -1) return;
      var columns = getComputedStyle(grid).display === 'grid'
        ? getComputedStyle(grid).gridTemplateColumns.split(' ').length
        : 1;
      var to = { ArrowRight: at + 1, ArrowLeft: at - 1, ArrowDown: at + columns, ArrowUp: at - columns, Home: 0, End: days.length - 1 }[event.key];
      if (to === undefined) return;
      event.preventDefault();
      to = Math.max(0, Math.min(days.length - 1, to));
      pick(days[to]);
      days[to].focus();
    });
  });
})();
